import {
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
} from "reactstrap";
import VTCMenu from "../../web/components/VTCMenu";
import Breadcrumbs from "../../web/components/Breadcrumbs";
import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast, ToastContainer } from "react-toastify";
import EnterpriseService from "../../common/services/enterprise-service";
import { confirmWrapper } from "../ui/askConfirm";
import truckyService from "../../common/services/trucky-service";
import { useNavigate, useParams } from "react-router";
import LoadingOverlay from "react-loading-overlay";
import { EnterpriseContext } from "./EnterpriseContext";
import ProfileCardV2 from "../ProfileCardV2";

export default function EditApplication({ container }) {
  const { globalContext } = useContext(EnterpriseContext);
  const [loadingData, setLoadingData] = useState(true);
  const [application, setApplication] = useState(null);
  const [activeTab, toggleTab] = useState("details");
  const [logs, setUserLogs] = useState([]);
  const es = new EnterpriseService();
  const navigate = useNavigate();
  const [contactMessage, setContactMessage] = useState({
    message: "",
    add_cc_email: globalContext.member.email,
  });

  const { id } = useParams();

  if (!es.can(globalContext.member, "members.manage_applications")) {
    return;
  }
  
  useEffect(() => {
    truckyService.setDocumentTitle("Applications");
    getApplication();
  }, []);

  const getApplication = async () => {
    setLoadingData(true);
    const result = await es.getApplication(globalContext.company, id);
    if (result.error) {
      toast.error(result.message);
    } else {
      setApplication(result);
      getUserLogs(result.user.id);
    }
    setLoadingData(false);
  };

  const getUserLogs = async (id) => {
    const result = await es.getUserLogs(id);
    setUserLogs(
      result.filter(
        (m) =>
          m.reason == "joined" ||
          m.reason == "kicked" ||
          m.reason == "got_kicked" ||
          m.reason == "left"
      )
    );
  };

  const accept = async () => {
    if (
      await confirmWrapper(
        container.localeManager.strings.applicationConfirmation,
        { container: container }
      )
    ) {
      setLoadingData(true);
      const result = await es.acceptApplication(
        globalContext.company,
        application
      );
      if (result.success) {
        toast.success("Application accepted successfully");
        getApplication();
      } else {
        toast.error(result.message);
      }
      setLoadingData(false);
    }
  };

  const reject = async () => {
    if (application.reject_reason != null && application.reject_reason != "") {
      if (
        await confirmWrapper(
          container.localeManager.strings.applicationRejection,
          { container: container }
        )
      ) {
        setLoadingData(true);
        const result = await es.updateApplication(
          globalContext.company,
          application
        );

        if (result.success) {
          const rejectResult = await es.rejectApplication(
            globalContext.company,
            application
          );

          if (rejectResult.success) {
            toast.success("Application rejected successfully");
            getApplication();
          } else {
            toast.error(result.message);
          }
        } else {
          toast.error(result.message);
        }
        setLoadingData(false);
      }
    } else {
      toast.error(container.localeManager.strings.pleaseFillRejectReason, {
        position: "top-right",
      });
    }
  };

  const save = async () => {
    setLoadingData(true);
    const result = await es.updateApplication(
      globalContext.company,
      application
    );
    if (result.success) {
      toast.success("Application saved successfully");
      getApplication();
    } else {
      toast.error(result.message);
    }
    setLoadingData(false);
  };

  const assignToMe = async () => {
    application.editor_user_id = globalContext.member.id;
    application.status = "in_progress";

    const result = await es.updateApplication(
      globalContext.company,
      application
    );

    if (result.success) {
      toast.success("Application assigned successfully");
      getApplication();
    } else {
      toast.error(result.message);
    }
  };

  const getLogMessage = (log) => {
    if (log.reason == "joined") {
      return container.localeManager.strings.formatString(
        container.localeManager.strings.companyJoined,
        log.company == null
          ? container.localeManager.strings.deletedCompany
          : log.company.name
      );
    }

    if (
      log.reason == "got_kicked" ||
      log.reason == "kicked" ||
      log.reason == "left"
    ) {
      return container.localeManager.strings.formatString(
        container.localeManager.strings.leftCompany,
        log.company == null
          ? container.localeManager.strings.deletedCompany
          : log.company.name
      );
    }
  };

  const sendApplicationContactMessage = async () => {
    const result = await es.sendApplicationContactMessage(
      company,
      application,
      contactMessage
    );

    if (result.success) {
      toast.success(container.localeManager.strings.messageSent);
    } else {
      toast.error(result.message);
    }
  };

  return (
    <LoadingOverlay
      active={loadingData}
      spinner={
        <Spinner
          color="primary"
          style={{ width: "4rem", height: "4rem" }}
        ></Spinner>
      }
    >
      <Row noGutters className="" id="enterpriseContainer">
        <VTCMenu active={"/applications"} container={container}></VTCMenu>

        <Col>
          <Row noGutters>
            <Col>
              <Card className="h-100">
                <CardBody>
                  <Breadcrumbs
                    breadcrumbs={[
                      {
                        to: "/vtchub",
                        title: container.localeManager.strings.vtcHub,
                      },
                      {
                        to: "/company/members",
                        title: container.localeManager.strings.members,
                      },
                      {
                        to: "/applications",
                        title: container.localeManager.strings.applications,
                      },
                      {
                        title: container.localeManager.strings.edit,
                      },
                    ]}
                  ></Breadcrumbs>
                  <div className="d-flex justify-content-between">
                    <h5>{container.localeManager.strings.applications}</h5>
                  </div>
                  {application &&
                    application.user.can_be_contacted_via_email && (
                      <Nav
                        className="nav-custom nav-line-tabs nav-line-tabs-2x fs-6 fw-bold mb-3"
                        tabs
                      >
                        <NavItem>
                          <NavLink
                            className={classNames({
                              active: activeTab === "details",
                            })}
                            onClick={() => {
                              toggleTab("details");
                            }}
                          >
                            {container.localeManager.strings.details}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classNames({
                              active: activeTab === "contact",
                            })}
                            onClick={() => {
                              toggleTab("contact");
                            }}
                          >
                            {container.localeManager.strings.contact}
                          </NavLink>
                        </NavItem>
                      </Nav>
                    )}
                  {application && (
                    <TabContent className="mt-3" activeTab={activeTab}>
                      <TabPane tabId="details">
                        <Row>
                          <Col sm={8}>
                            <ProfileCardV2
                              user={application.user}
                              container={container}
                              avatarSize={100}
                            />
                            {application.user.steam_profile != null && (
                              <Row className="my-3">
                                <Col sm={6}>
                                  <Button
                                    tag="a"
                                    size="sm"
                                    color="secondary"
                                    target="_blank"
                                    href={`https://steamcommunity.com/profiles/${application.user.steam_profile.steam_id}`}
                                  >
                                    <FontAwesomeIcon
                                      icon={["fab", "steam"]}
                                    ></FontAwesomeIcon>
                                    {
                                      container.localeManager.strings
                                        .viewSteamProfile
                                    }
                                  </Button>
                                </Col>
                                <Col sm={6} className="text-end">
                                  <Button
                                    tag="a"
                                    size="sm"
                                    color="secondary"
                                    target="_blank"
                                    href={`/user/${application.user.id}`}
                                  >
                                    {
                                      container.localeManager.strings
                                        .profileDetails
                                    }
                                  </Button>
                                </Col>
                              </Row>
                            )}
                            {application.discord != null && (
                              <FormGroup>
                                <Label>Discord: {application.discord}</Label>
                              </FormGroup>
                            )}

                            {application.questionnaire &&
                              Object.keys(application.questionnaire).length >
                                0 &&
                              Object.keys(application.questionnaire).map(
                                (key) => {
                                  return (
                                    <div>
                                      <Label>
                                        {
                                          application.questionnaire[key]
                                            .question
                                        }
                                      </Label>
                                      <div
                                        className="mb-3 border rounded p-3"
                                        style={{
                                          wordBreak: "break-word",
                                          whiteSpace: "pre-wrap",
                                          overflowWrap: "break-word",
                                        }}
                                      >
                                        {application.questionnaire[key].answer}
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            <FormGroup>
                              <Label>
                                {
                                  container.localeManager.strings
                                    .messageFromTheUser
                                }
                              </Label>
                              <div
                                className="mb-3 border rounded p-3"
                                style={{
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {application.message}
                              </div>
                            </FormGroup>
                            <FormGroup>
                              <Label>
                                {container.localeManager.strings.rejectReason}
                              </Label>
                              <textarea
                                className="form-control"
                                rows="5"
                                onChange={(e) =>
                                  setApplication({
                                    ...application,
                                    reject_reason: e.target.value,
                                  })
                                }
                              >
                                {application.reject_reason}
                              </textarea>
                            </FormGroup>
                            <FormGroup>
                              <Label>
                                {
                                  container.localeManager.strings
                                    .internalComment
                                }
                              </Label>
                              <textarea
                                className="form-control"
                                rows="5"
                                onChange={(e) =>
                                  setApplication({
                                    ...application,
                                    internal_comment: e.target.value,
                                  })
                                }
                              >
                                {application.internal_comment}
                              </textarea>
                              <FormText className="text-muted">
                                {
                                  container.localeManager.strings
                                    .internalCommentHelpText
                                }
                              </FormText>
                            </FormGroup>
                            <FormText className="text-muted">
                              <FontAwesomeIcon icon="info-circle"></FontAwesomeIcon>
                              {
                                container.localeManager.strings
                                  .assignToMeHelpText
                              }
                            </FormText>
                            <FormGroup className="mt-3">
                              <div className="d-flex justify-content-between">
                                <div>
                                  <Button
                                    size="sm"
                                    className="me-1"
                                    onClick={() => save()}
                                    color="primary"
                                  >
                                    <FontAwesomeIcon icon="save"></FontAwesomeIcon>
                                    {container.localeManager.strings.save}
                                  </Button>
                                  {application.editor_user_id == null &&
                                    application.status != "retired" && (
                                      <Button
                                        size="sm"
                                        className="me-1"
                                        onClick={() => assignToMe()}
                                        color="primary"
                                      >
                                        <FontAwesomeIcon icon="play"></FontAwesomeIcon>
                                        {
                                          container.localeManager.strings
                                            .assignToMe
                                        }
                                      </Button>
                                    )}
                                </div>
                                {(application.status == "pending" ||
                                  application.status == "in_progress") && (
                                  <div>
                                    <Button
                                      size="sm"
                                      className="me-1"
                                      onClick={() =>
                                        navigate(
                                          `/messenger?user_id=${application.user.id}`
                                        )
                                      }
                                      color="primary"
                                    >
                                      <FontAwesomeIcon icon="comment-dots"></FontAwesomeIcon>
                                      {
                                        container.localeManager.strings
                                          .directMessage
                                      }
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="me-1"
                                      onClick={() => reject()}
                                      color="danger"
                                    >
                                      <FontAwesomeIcon icon="times"></FontAwesomeIcon>
                                      {container.localeManager.strings.reject}
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => accept()}
                                      className="me-1"
                                      color="success"
                                    >
                                      <FontAwesomeIcon icon="check"></FontAwesomeIcon>
                                      {container.localeManager.strings.accept}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </FormGroup>
                          </Col>
                          <Col>
                            {logs.length > 0 && (
                              <div className="timeline-label user-activities">
                                {logs.map((m) => {
                                  return (
                                    <div className="timeline-item">
                                      <div className="timeline-label fw-bolder text-gray-800 fs-6">
                                        {container.localeManager
                                          .moment(m.created_at)
                                          .format("L")}
                                      </div>
                                      <div className="timeline-badge">
                                        <FontAwesomeIcon
                                          icon="genderless"
                                          className={`${
                                            m.reason == "joined"
                                              ? "text-success"
                                              : "text-danger"
                                          } fs-1`}
                                        ></FontAwesomeIcon>
                                      </div>
                                      <div className="timeline-content d-flex">
                                        <span className="fw-bolder text-gray-800 ps-3">
                                          {getLogMessage(m)}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {logs.length == 0 &&
                              container.localeManager.strings.noActiveLogs}
                          </Col>
                        </Row>
                      </TabPane>
                      <TabPane tabId="contact">
                        <Row>
                          <Col>
                            <FormText color="muted" className="mb-3">
                              {
                                container.localeManager.strings
                                  .applicationContactHint
                              }
                            </FormText>
                            <FormGroup>
                              <Label>
                                {container.localeManager.strings.message}
                              </Label>
                              <textarea
                                className="form-control"
                                rows="5"
                                onChange={(e) =>
                                  setContactMessage({
                                    ...contactMessage,
                                    message: e.target.value,
                                  })
                                }
                              >
                                {contactMessage.message}
                              </textarea>
                            </FormGroup>
                            <FormGroup>
                              <Label>
                                {container.localeManager.strings.addCcEmail}
                              </Label>
                              <InputGroup>
                                <Input
                                  value={contactMessage.add_cc_email}
                                  type="email"
                                  onChange={(e) =>
                                    setContactMessage({
                                      ...contactMessage,
                                      add_cc_email: e.target.value,
                                    })
                                  }
                                ></Input>
                                <Button
                                  onClick={() =>
                                    setContactMessage({
                                      ...contactMessage,
                                      add_cc_email: "",
                                    })
                                  }
                                  color="danger"
                                >
                                  {container.localeManager.strings.noThanks}
                                </Button>
                              </InputGroup>
                              <FormText color="muted">
                                {container.localeManager.strings.addCcEmailHint}
                              </FormText>
                            </FormGroup>
                            {contactMessage.message.trim() != "" && (
                              <FormGroup>
                                <Button
                                  onClick={sendApplicationContactMessage}
                                  color="primary"
                                >
                                  {
                                    container.localeManager.strings
                                      .sendMessageToUser
                                  }
                                </Button>
                              </FormGroup>
                            )}
                          </Col>
                        </Row>
                      </TabPane>
                    </TabContent>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </LoadingOverlay>
  );
}
